import React, { useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import useResponsive from "../../hooks/useResponsive";

interface ExpenseTypeBreakdownProps {
  expenses: Array<{
    id: string;
    name: string;
    monthlyAmount: number;
    expenseType?: "fixed" | "percentage" | "hybrid" | "customer_based";
  }>;
  currentDate?: Date;
}

const EXPENSE_TYPE_COLORS = {
  fixed: "#3b82f6", // Blue - Fixed costs
  percentage: "#f59e0b", // Amber - Variable/percentage costs
  hybrid: "#8b5cf6", // Purple - Hybrid costs
  customer_based: "#10b981", // Emerald - Customer-based costs
};

const ExpenseTypeBreakdownChart: React.FC<ExpenseTypeBreakdownProps> = ({
  expenses,
  currentDate = new Date(),
}) => {
  const { isMobile, isTablet } = useResponsive();

  // Responsive chart sizing
  const chartConfig = useMemo(() => {
    if (isMobile) {
      return {
        cx: "50%",
        cy: "45%",
        innerRadius: 20,
        outerRadius: 50,
        legendHeight: 60, // More space for mobile legend
        showLegend: false, // Hide legend on mobile to save space
      };
    } else if (isTablet) {
      return {
        cx: "50%",
        cy: "42%",
        innerRadius: 22,
        outerRadius: 55,
        legendHeight: 50,
        showLegend: true,
      };
    } else {
      return {
        cx: "50%",
        cy: "40%",
        innerRadius: 25,
        outerRadius: 60,
        legendHeight: 40,
        showLegend: true,
      };
    }
  }, [isMobile, isTablet]);

  const chartData = useMemo(() => {
    const typeTotals: Record<string, number> = {};

    expenses.forEach((expense) => {
      const type = expense.expenseType || "fixed";
      const amount = expense.monthlyAmount || 0;

      if (amount > 0) {
        typeTotals[type] = (typeTotals[type] || 0) + amount;
      }
    });

    return Object.entries(typeTotals)
      .map(([type, amount]) => ({
        name:
          type === "fixed"
            ? "Fixed Costs"
            : type === "percentage"
            ? "Variable Costs"
            : type === "hybrid"
            ? "Hybrid Costs"
            : "Customer-Based Costs",
        value: amount,
        color:
          EXPENSE_TYPE_COLORS[type as keyof typeof EXPENSE_TYPE_COLORS] ||
          "#6b7280",
      }))
      .sort((a, b) => b.value - a.value);
  }, [expenses]);

  const totalExpenses = chartData.reduce((sum, item) => sum + item.value, 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percentage = ((data.value / totalExpenses) * 100).toFixed(1);

      return (
        <div className="bg-surface-elevated text-white text-xs px-3 py-2 rounded-lg shadow-glow">
          <p className="font-semibold">{data.payload.name}</p>
          <p className="text-blue-400">${data.value.toLocaleString()}/month</p>
          <p className="text-white/55">{percentage}% of total</p>
        </div>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <div className="bg-surface-elevated rounded-lg border h-full flex flex-col">
        <div className="px-4 py-3 border-b bg-white/[0.06] flex-shrink-0">
          <h4 className="text-sm font-medium text-white/75">Expense Types</h4>
          <p className="text-xs text-white/45 mt-1">No expense data</p>
        </div>
        <div className="p-4 flex-1 flex items-center justify-center">
          <p className="text-white/45 text-sm">No expense data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-elevated rounded-lg border h-full flex flex-col">
      <div className="px-4 py-3 border-b bg-white/[0.06] flex-shrink-0">
        <h4 className="text-sm font-medium text-white/75">Expense Types</h4>
        <p className="text-xs text-white/45 mt-1">
          {currentDate.toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
          })}
          : ${totalExpenses.toLocaleString()}/month
        </p>
      </div>

      <div className="p-4 flex-1 min-h-0 pb-6">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx={chartConfig.cx}
              cy={chartConfig.cy}
              innerRadius={chartConfig.innerRadius}
              outerRadius={chartConfig.outerRadius}
              paddingAngle={2}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            {chartConfig.showLegend && (
              <Legend
                verticalAlign="bottom"
                height={chartConfig.legendHeight}
                formatter={(value, entry) => {
                  if (isMobile) {
                    // Shorter format for mobile
                    return `${value.split(" ")[0]}`;
                  }
                  return `${value} ($${(
                    entry.payload?.value || 0
                  ).toLocaleString()})`;
                }}
                wrapperStyle={{
                  fontSize: isMobile ? "9px" : "10px",
                  lineHeight: "1.2",
                  paddingTop: "4px",
                  paddingBottom: "8px",
                }}
              />
            )}
          </PieChart>
        </ResponsiveContainer>

        {/* Mobile Legend Alternative - Show below chart */}
        {isMobile && chartData.length > 0 && (
          <div className="mt-2 space-y-1">
            {chartData.map((entry, index) => (
              <div
                key={index}
                className="flex items-center justify-between text-xs"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-white/75">{entry.name}</span>
                </div>
                <span className="text-white/65 font-medium">
                  ${entry.value.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpenseTypeBreakdownChart;
